import { func } from "prop-types";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

import styled from "styled-components/macro";

import { useIsLoggedIn } from "Flocknote/stateHooks";

import { useManageMyGivingPage } from "Giving/ManageMyGivingPage/ManageMyGivingPageContext";
import { useIsFeatureFlagEnabled } from "Giving/ManageMyGivingPage/useIsFeatureFlagEnabled";
import classNames from "classnames";

import RecentGifts from "./GiftsAndPledges/RecentGifts";
import GivingSummary from "./GivingSummary";
import ManageMyGivingPageAuth from "./ManageMyGivingPageAuth";
import NonHeadOfHousehold from "./NonHeadOfHousehold";

const PrimaryContent = ({ setRecentGiftsLoaded }) => {
  const isLoggedIn = useIsLoggedIn();
  const { state } = useManageMyGivingPage();
  const isHoH = state.user?.isHoHOrSpouseOfHoH;

  if (!isLoggedIn) return <ManageMyGivingPageAuth />;

  if (isLoggedIn && isHoH)
    return (
      <ErrorBoundary fallbackRender={() => null}>
        <GivingSummary />
        <RecentGifts setLoaded={setRecentGiftsLoaded} />
      </ErrorBoundary>
    );

  if (isLoggedIn && !isHoH) return <NonHeadOfHousehold />;

  return null;
};

PrimaryContent.propTypes = { setRecentGiftsLoaded: func };

export default function RightColumn() {
  const featureFlagEnabled = useIsFeatureFlagEnabled();
  const { state } = useManageMyGivingPage();

  const colorSchemeIsLight = state.pageSettings?.colorSchemeIsLight;
  const [fixedTop, setFixedTop] = useState(false);
  const [fixedBottom, setFixedBottom] = useState(false);

  const [recentGiftsLoaded, setRecentGiftsLoaded] = useState(false);

  const measurements = useRef();
  const contentRef = useRef();

  const lastScrollPosition = useRef(0);
  const lastScrollDirection = useRef("down");

  const [topPx, setTopPx] = useState(false);
  const topPxRef = useRef(false);

  useEffect(() => {
    //window.addEventListener("resize", measureForScroll);
    window.addEventListener("scroll", measureForScroll);
    return () => {
      // window.removeEventListener("resize", measureForScroll);
      window.removeEventListener("scroll", measureForScroll);
    };
  }, []);

  useEffect(() => {
    measureForScroll();
  }, [recentGiftsLoaded]);

  const measureForScroll = (e) => {
    const scrollDirection =
      window.scrollY === lastScrollPosition.current
        ? false
        : window.scrollY > lastScrollPosition.current
        ? "down"
        : "up";

    if (!scrollDirection) return;

    lastScrollPosition.current = window.scrollY;

    measurements.current = contentRef.current?.getBoundingClientRect();

    if (measurements.current.height < window.innerHeight) {
      setFixedTop(true);
      return;
    }

    const difference = window.scrollY + measurements.current.top;

    if (lastScrollDirection.current !== scrollDirection) {
      lastScrollDirection.current = scrollDirection;

      if (
        scrollDirection === "up" &&
        window.innerHeight === measurements.current.bottom
      ) {
        setFixedBottom(false);
        setTopPx(difference);
      } else if (
        scrollDirection === "down" &&
        window.scrollY > 0 &&
        measurements.current.top === 0
      ) {
        setFixedTop(false);
        setTopPx(window.scrollY);
      }
    }
    if (scrollDirection === "down") {
      const shouldFixBottom =
        window.scrollY + window.innerHeight >=
        measurements.current.height + (topPxRef.current ? topPxRef.current : 0);
      setFixedBottom(shouldFixBottom);
    } else if (scrollDirection === "up") {
      const shouldFixTop = measurements.current.top >= 0 && window.scrollY > 0;
      setFixedTop(shouldFixTop);
      if (window.scrollY <= 0) {
        setTopPx(false);
      }
    }
  };

  useLayoutEffect(() => {
    if (fixedBottom) {
      setFixedTop(false);
    }
  }, [fixedBottom]);

  useLayoutEffect(() => {
    if (fixedTop) {
      setFixedBottom(false);
    }
  }, [fixedTop]);

  useEffect(() => {
    topPxRef.current = topPx;
  }, [topPx]);

  if (!featureFlagEnabled) {
    return null;
  }

  return (
    <Wrapper>
      <Content
        ref={contentRef}
        className={classNames({
          fixed_top: fixedTop,
          fixed_bottom: fixedBottom,
          right_column_content: true,
        })}
        style={{ top: topPx }}
      >
        <PaddingTop />
        <PrimaryContent setRecentGiftsLoaded={setRecentGiftsLoaded} />
        <Padding />
      </Content>
      <Background $isLight={colorSchemeIsLight} />
    </Wrapper>
  );
}

const Background = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  width: max(calc((100% - 1440px) / 2 + 370px), 370px);

  background: ${({ $isLight }) =>
    $isLight ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.4)"};
  z-index: -1;
`;

const Wrapper = styled.div`
  width: 340px;
  flex-shrink: 0;
  flex-grow: 0;
  position: relative;
  height: 100vh;
`;

const Content = styled.div`
  width: 340px;
  position: absolute;
  &.fixed_top {
    position: fixed;
    top: 0 !important;
    bottom: auto !important;
  }
  &.fixed_bottom {
    position: fixed;
    bottom: 0;
    top: auto !important;
  }
`;
const Padding = styled.div`
  height: 40px;
`;
const PaddingTop = styled.div`
  height: 80px;
`;
